<template>
  <sp-button
    class="sp-stepper-item"
    :disabled="!editable"
    stacked
    variant="text"
    color="default"
    :class="rootClassModifiers"
  >
    <slot>
      <div class="container">
        <div class="sp-stepper-item__title" :class="classModifiers">{{ title }}</div>
        <div class="sp-stepper-item__status" :class="classModifiers">
          <sp-icon name="check-filled" size="micro" fill-color="white"></sp-icon>
        </div>
        <hr v-if="showPrependDivider" class="sp-stepper-item__divider --prepend" part="stepper-item-divider-prepend" />
        <hr v-if="showAppendDivider" class="sp-stepper-item__divider --append" part="stepper-item-divider-append" />
      </div>
    </slot>
  </sp-button>
</template>

<script setup>
import { computed, ref, useAttrs, watch } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { booleanAttribute, toBoolean } from "#ce/utils/props";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  value: {
    type: [String, Number, Object, Array, Boolean],
    default: undefined,
  },
  active: booleanAttribute(),
  completed: booleanAttribute(),
  editable: booleanAttribute(),
  isFirst: booleanAttribute(),
  isLast: booleanAttribute(),
});

const attrs = useAttrs();
const title = computed(() => attrs.title);

const editable = ref(toBoolean(props.editable));
watch(
  () => props.editable,
  () => (editable.value = toBoolean(props.editable)),
);
const { gtModifiers } = useBreakpoints();

const classModifiers = computed(() => ({
  "--active": toBoolean(props.active),
  "--completed": toBoolean(props.completed),
}));

const rootClassModifiers = computed(() => [...gtModifiers.value]);

const showPrependDivider = computed(() => !toBoolean(props.isFirst));
const showAppendDivider = computed(() => !toBoolean(props.isLast));
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-stepper-item {
  --stepper-item-status-color: var(--sp-comp-stepper-item-status-color, var(--sp-sys-color-outline-variant));
  --stepper-item-status-color-active: var(--sp-comp-stepper-item-status-color-active, var(--sp-sys-color-progress));
  --stepper-item-status-color-completed: var(
    --sp-comp-stepper-item-status-color-completed,
    var(--stepper-item-status-color-active)
  );
  --stepper-item-status-size: 1.375rem;
  --stepper-item-title-display: var(--sp-comp-stepper-item-title-on-narrow, none);
  --stepper-item-title-color: var(--sp-comp-stepper-item-text-color, var(--sp-sys-color-outline));
  --sp-comp-button-disabled-background-color: transparent;

  flex-direction: column;
  align-items: center;
  flex: 1;
  text-align: center;
  display: grid;
  position: relative;

  .container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &.--gt-sm-screen {
    --stepper-item-title-display: var(--sp-comp-stepper-item-title-on-wide, block);
  }
}

.sp-stepper-item__divider {
  display: block;
  width: calc(50% + var(--header-stepper-items-gap) / 2);
  height: 2px;
  position: absolute;
  border: none;
  box-shadow: none;
  margin: 0;
  bottom: calc(var(--stepper-item-status-size) / 2);
  z-index: 1;

  &.--prepend {
    left: calc(var(--header-stepper-items-gap) / -2);
    background: var(--stepper-item-divider-prepend-color, var(--sp-sys-color-outline-variant));
  }

  &.--append {
    right: calc(var(--header-stepper-items-gap) / -2);
    background: var(--stepper-item-divider-append-color, var(--sp-sys-color-outline-variant));
  }
}

.sp-stepper-item__status {
  width: var(--stepper-item-status-size);
  height: var(--stepper-item-status-size);
  border-radius: 50%;
  background-color: var(--stepper-item-status-color);
  margin-top: var(--sp-ref-scale-6);
  z-index: 3;
  color: currentColor;
  display: flex;
  align-items: center;
  justify-content: center;

  sp-icon {
    opacity: 0;
  }

  &.--active {
    --stepper-item-status-color: var(--stepper-item-status-color-active);

    color: white;

    + .sp-stepper-item__divider {
      --stepper-item-divider-prepend-color: var(--stepper-item-status-color-active);
    }
  }

  &.--completed {
    --stepper-item-status-color: var(--stepper-item-status-color-completed);
    color: white;

    + .sp-stepper-item__divider,
    + .sp-stepper-item__divider + .sp-stepper-item__divider {
      --stepper-item-divider-prepend-color: var(--stepper-item-status-color-completed);
      --stepper-item-divider-append-color: var(--stepper-item-status-color-completed);
    }

    sp-icon {
      opacity: 1;
      --fill: white;
    }
  }
}

.sp-stepper-item__title {
  display: var(--stepper-item-title-display);
  font: var(--sp-sys-typescale-footnote-bold-short);
  color: var(--stepper-item-title-color);

  &.--active,
  &.--completed {
    --stepper-item-title-color: var(--stepper-item-status-color-active);
  }
}

sp-button {
  --sp-comp-button-disabled-opacity: 1;
  --sp-comp-button-text-transform: var(--sp-comp-stepper-item-title-text-transform, uppercase);
  --sp-comp-button-font-size: 0.8125rem;
  --sp-comp-button-font-family: var(--sp-sys-font-family-medium);
  --sp-comp-button-font-weight: var(--sp-sys-font-weight-medium);
  --sp-comp-button-gap: 0;
  --sp-comp-button-bg-color: transparent;

  &::part(base) {
    padding: 0 !important;
    min-width: fit-content;
  }
}
</style>
